import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { 
  Truck, 
  Package, 
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  ArrowRight,
  AlertTriangle,
  Split,
  PauseCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoadingConsolePage = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [todayPlans, setTodayPlans] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Loading dialog
  const [loadingDialog, setLoadingDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingData, setLoadingData] = useState({
    vehicle_number: '',
    driver_name: '',
    driver_contact: '',
    transporter_name: '',
    items: {}  // { item_id: { actual_quantity, num_packages, variance_reason } }
  });
  const [submitting, setSubmitting] = useState(false);

  // Shortfall dialog state
  const [shortfallDialog, setShortfallDialog] = useState(false);
  const [shortfallData, setShortfallData] = useState({
    planId: null,
    planNumber: '',
    items: [],  // Items with shortfall: { item_id, item_name, planned, actual, shortfall }
    totalShortfall: 0
  });
  const [postponeReason, setPostponeReason] = useState('');
  const [shortfallSubmitting, setShortfallSubmitting] = useState(false);

  const fetchTodayPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/dispatch-plans/today`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setTodayPlans(data);
      }
    } catch (err) {
      console.error('Failed to fetch today plans:', err);
    }
  }, [getAuthHeader]);

  const fetchAllPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/dispatch-plans?status=planned&limit=50`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setAllPlans(data);
      }
    } catch (err) {
      console.error('Failed to fetch all plans:', err);
    }
  }, [getAuthHeader]);

  const fetchSummary = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`${API_URL}/api/dispatch-plans/summary?date_from=${today}&date_to=${today}`, {
        headers: getAuthHeader()
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [getAuthHeader]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchTodayPlans(), fetchAllPlans(), fetchSummary()]);
    setLoading(false);
  }, [fetchTodayPlans, fetchAllPlans, fetchSummary]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const openLoadingDialog = (plan) => {
    setSelectedPlan(plan);
    
    // Initialize items with planned quantities and zero packages
    const itemsData = {};
    plan.items?.forEach(item => {
      itemsData[item.id] = {
        actual_quantity: item.planned_quantity,
        num_packages: 0,
        variance_reason: ''
      };
    });
    
    setLoadingData({
      vehicle_number: plan.vehicle_number || '',
      driver_name: plan.driver_name || '',
      driver_contact: plan.driver_contact || '',
      transporter_name: plan.transporter_name || '',
      items: itemsData
    });
    setLoadingDialog(true);
  };

  const handleItemQtyChange = (itemId, value) => {
    const qty = parseFloat(value) || 0;
    setLoadingData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: {
          ...prev.items[itemId],
          actual_quantity: qty
        }
      }
    }));
  };

  const handleItemPackagesChange = (itemId, value) => {
    const pkgs = parseInt(value) || 0;
    setLoadingData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: {
          ...prev.items[itemId],
          num_packages: pkgs
        }
      }
    }));
  };

  const handleVarianceReasonChange = (itemId, value) => {
    setLoadingData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemId]: {
          ...prev.items[itemId],
          variance_reason: value
        }
      }
    }));
  };

  const hasVariance = (item) => {
    const actualQty = loadingData.items[item.id]?.actual_quantity || 0;
    return actualQty !== item.planned_quantity;
  };

  const handleMarkReady = async () => {
    if (!loadingData.vehicle_number.trim()) {
      toast.error('Vehicle number is required');
      return;
    }

    // Check variance reasons
    const itemsWithVariance = selectedPlan.items?.filter(item => hasVariance(item)) || [];
    const missingReasons = itemsWithVariance.filter(item => 
      !loadingData.items[item.id]?.variance_reason?.trim()
    );
    
    if (missingReasons.length > 0) {
      toast.error('Please provide variance reason for items with different actual quantity');
      return;
    }

    setSubmitting(true);
    try {
      // Calculate total packages
      const totalPackages = Object.values(loadingData.items).reduce(
        (sum, item) => sum + (item.num_packages || 0), 0
      );

      const payload = {
        vehicle_number: loadingData.vehicle_number.toUpperCase(),
        driver_name: loadingData.driver_name,
        driver_contact: loadingData.driver_contact,
        transporter_name: loadingData.transporter_name,
        total_packages: totalPackages,
        items: Object.entries(loadingData.items).map(([id, data]) => ({
          id,
          actual_quantity: data.actual_quantity,
          num_packages: data.num_packages || 0,
          variance_reason: data.variance_reason || null
        }))
      };

      const res = await fetch(`${API_URL}/api/dispatch-plans/${selectedPlan.id}/ready-for-dispatch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        
        // Check for shortfalls (actual < planned)
        const shortfallItems = [];
        selectedPlan.items?.forEach(item => {
          const actualQty = loadingData.items[item.id]?.actual_quantity || 0;
          const plannedQty = item.planned_quantity || 0;
          const shortfall = plannedQty - actualQty;
          
          if (shortfall > 0) {
            shortfallItems.push({
              item_id: item.item_id,
              item_name: item.item_name,
              item_code: item.item_code,
              planned: plannedQty,
              actual: actualQty,
              shortfall: shortfall,
              unit: item.unit
            });
          }
        });
        
        if (shortfallItems.length > 0) {
          // There are shortfalls - show the shortfall dialog
          const totalShortfall = shortfallItems.reduce((sum, i) => sum + i.shortfall, 0);
          setShortfallData({
            planId: selectedPlan.id,
            planNumber: data.dispatch_plan_number || selectedPlan.dispatch_plan_number,
            gatePassNumber: data.gate_pass_number,
            items: shortfallItems,
            totalShortfall
          });
          setPostponeReason('');
          setLoadingDialog(false);
          setShortfallDialog(true);
          
          toast.success(
            <div>
              <p>Vehicle loaded with partial quantity</p>
              <p className="text-sm">Gate Pass: <strong>{data.gate_pass_number}</strong></p>
              <p className="text-sm text-orange-600">Shortfall detected - please choose action</p>
            </div>
          );
        } else {
          // No shortfalls - complete normally
          toast.success(
            <div>
              <p>Dispatch Plan ready!</p>
              <p className="text-sm">Gate Pass: <strong>{data.gate_pass_number}</strong></p>
            </div>
          );
          setLoadingDialog(false);
        }
        
        fetchAllData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to mark ready');
      }
    } catch (err) {
      toast.error('Failed to mark ready for dispatch');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle "Load in Another Vehicle" - creates a split plan
  const handleCreateSplitPlan = async () => {
    setShortfallSubmitting(true);
    try {
      const payload = {
        items: shortfallData.items.map(item => ({
          item_id: item.item_id,
          shortfall_quantity: item.shortfall
        })),
        remarks: `Split from ${shortfallData.planNumber} - shortfall items`
      };

      const res = await fetch(`${API_URL}/api/dispatch-plans/${shortfallData.planId}/split`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(
          <div>
            <p>Split Plan Created!</p>
            <p className="text-sm">New Plan: <strong>{data.split_plan_number}</strong></p>
            <p className="text-sm">Quantity: {data.total_split_quantity} units</p>
          </div>
        );
        setShortfallDialog(false);
        fetchAllData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create split plan');
      }
    } catch (err) {
      toast.error('Failed to create split plan');
    } finally {
      setShortfallSubmitting(false);
    }
  };

  // Handle "Request Postponement" - notifies Sales team
  const handleRequestPostponement = async () => {
    if (!postponeReason.trim()) {
      toast.error('Please provide a reason for postponement');
      return;
    }

    setShortfallSubmitting(true);
    try {
      const payload = {
        reason: postponeReason,
        items: shortfallData.items.map(item => ({
          item_id: item.item_id,
          shortfall_quantity: item.shortfall
        }))
      };

      const res = await fetch(`${API_URL}/api/dispatch-plans/${shortfallData.planId}/request-postponement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(
          <div>
            <p>Postponement Requested</p>
            <p className="text-sm">Sales team will be notified</p>
          </div>
        );
        setShortfallDialog(false);
        fetchAllData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to request postponement');
      }
    } catch (err) {
      toast.error('Failed to request postponement');
    } finally {
      setShortfallSubmitting(false);
    }
  };

  // Skip shortfall handling - just close dialog
  const handleSkipShortfall = () => {
    setShortfallDialog(false);
    toast.info('Shortfall handling skipped. You can create a new plan later if needed.');
  };

  const getTotalActualQty = () => {
    return Object.values(loadingData.items).reduce((sum, item) => sum + (item.actual_quantity || 0), 0);
  };

  const getTotalPackages = () => {
    return Object.values(loadingData.items).reduce((sum, item) => sum + (item.num_packages || 0), 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      planned: 'bg-yellow-100 text-yellow-800',
      ready_for_dispatch: 'bg-blue-100 text-blue-800',
      invoiced: 'bg-purple-100 text-purple-800',
      dispatched: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="loading-console-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Loading Console</h1>
          <p className="text-gray-500">Load vehicles and mark ready for dispatch</p>
        </div>
        <Button onClick={fetchAllData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Today Planned</p>
                <p className="text-2xl font-bold">{todayPlans.filter(p => p.status === 'planned').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Truck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Ready for Dispatch</p>
                <p className="text-2xl font-bold">{todayPlans.filter(p => p.status === 'ready_for_dispatch').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">All Pending</p>
                <p className="text-2xl font-bold">{allPlans.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Today Total Items</p>
                <p className="text-2xl font-bold">
                  {todayPlans.reduce((sum, p) => sum + (p.items?.length || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Plans */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Today's Dispatch Plans
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : todayPlans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Truck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No dispatch plans scheduled for today</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {todayPlans.map(plan => (
                <AccordionItem key={plan.id} value={plan.id} className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-4">
                        <span className="font-bold">{plan.dispatch_plan_number}</span>
                        <Badge className={getStatusBadge(plan.status)}>
                          {plan.status_label || plan.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{plan.customer_name}</span>
                        <span>{plan.items?.length || 0} items</span>
                        <span>Qty: {plan.total_planned_quantity}</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pt-4 space-y-4">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>SO</TableHead>
                            <TableHead>Item</TableHead>
                            <TableHead className="text-right">Planned Qty</TableHead>
                            <TableHead>Unit</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {plan.items?.map(item => (
                            <TableRow key={item.id}>
                              <TableCell className="text-sm">{item.so_number}</TableCell>
                              <TableCell>
                                <div className="font-medium">{item.item_name}</div>
                                {item.item_code && (
                                  <div className="text-xs text-gray-500">{item.item_code}</div>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-medium">{item.planned_quantity}</TableCell>
                              <TableCell>{item.unit}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {plan.status === 'planned' && (
                        <div className="flex justify-end">
                          <Button onClick={() => openLoadingDialog(plan)} data-testid={`load-btn-${plan.id}`}>
                            <Truck className="h-4 w-4 mr-2" />
                            Load & Mark Ready
                          </Button>
                        </div>
                      )}
                      
                      {plan.status === 'ready_for_dispatch' && (
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div>
                            <p className="font-medium">Ready for Dispatch</p>
                            <p className="text-sm text-gray-600">
                              Vehicle: {plan.vehicle_number} | Gate Pass: {plan.gate_pass_number}
                            </p>
                          </div>
                          <Badge className="bg-blue-100 text-blue-800">Awaiting Invoice</Badge>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* All Pending Plans */}
      {allPlans.filter(p => !todayPlans.find(tp => tp.id === p.id)).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Other Pending Plans
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan No.</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Planned Date</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allPlans
                  .filter(p => !todayPlans.find(tp => tp.id === p.id))
                  .map(plan => (
                    <TableRow key={plan.id}>
                      <TableCell className="font-medium">{plan.dispatch_plan_number}</TableCell>
                      <TableCell>{plan.customer_name}</TableCell>
                      <TableCell>{formatDate(plan.planned_dispatch_date)}</TableCell>
                      <TableCell className="text-right">{plan.items?.length || 0}</TableCell>
                      <TableCell className="text-right">{plan.total_planned_quantity}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openLoadingDialog(plan)}
                        >
                          Load
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Loading Dialog */}
      <Dialog open={loadingDialog} onOpenChange={setLoadingDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Load Vehicle - {selectedPlan?.dispatch_plan_number}</DialogTitle>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="space-y-6">
              {/* Plan Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Customer:</span> <strong>{selectedPlan.customer_name}</strong></div>
                  <div><span className="text-gray-500">Date:</span> <strong>{selectedPlan.planned_dispatch_date}</strong></div>
                </div>
              </div>

              {/* Transport Details */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Transport Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vehicle Number *</Label>
                    <Input
                      value={loadingData.vehicle_number}
                      onChange={(e) => setLoadingData(prev => ({ ...prev, vehicle_number: e.target.value.toUpperCase() }))}
                      placeholder="MH12AB1234"
                      data-testid="vehicle-input"
                    />
                  </div>
                  <div>
                    <Label>Driver Name</Label>
                    <Input
                      value={loadingData.driver_name}
                      onChange={(e) => setLoadingData(prev => ({ ...prev, driver_name: e.target.value }))}
                      placeholder="Driver name"
                    />
                  </div>
                  <div>
                    <Label>Driver Contact</Label>
                    <Input
                      value={loadingData.driver_contact}
                      onChange={(e) => setLoadingData(prev => ({ ...prev, driver_contact: e.target.value }))}
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <Label>Transporter</Label>
                    <Input
                      value={loadingData.transporter_name}
                      onChange={(e) => setLoadingData(prev => ({ ...prev, transporter_name: e.target.value }))}
                      placeholder="Transport company"
                    />
                  </div>
                </div>
              </div>

              {/* Items with Actual Qty */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Items - Enter Actual Loaded Quantities & Packages
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Planned</TableHead>
                      <TableHead className="text-right w-24">Actual *</TableHead>
                      <TableHead className="text-right w-20">Pkgs *</TableHead>
                      <TableHead className="w-40">Variance Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedPlan.items?.map(item => {
                      const variance = hasVariance(item);
                      return (
                        <TableRow key={item.id} className={variance ? 'bg-yellow-50' : ''}>
                          <TableCell>
                            <div className="font-medium">{item.item_name}</div>
                            <div className="text-xs text-gray-500">{item.so_number}</div>
                          </TableCell>
                          <TableCell className="text-right">{item.planned_quantity} {item.unit}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={loadingData.items[item.id]?.actual_quantity || ''}
                              onChange={(e) => handleItemQtyChange(item.id, e.target.value)}
                              className="w-20 text-right"
                              min="0"
                              data-testid={`actual-qty-${item.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              value={loadingData.items[item.id]?.num_packages || ''}
                              onChange={(e) => handleItemPackagesChange(item.id, e.target.value)}
                              className="w-16 text-right"
                              min="0"
                              placeholder="0"
                              data-testid={`num-packages-${item.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            {variance && (
                              <Input
                                value={loadingData.items[item.id]?.variance_reason || ''}
                                onChange={(e) => handleVarianceReasonChange(item.id, e.target.value)}
                                placeholder="Reason required"
                                className="text-sm"
                                data-testid={`variance-reason-${item.id}`}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="flex justify-end gap-6 text-sm">
                  <div>
                    <span className="text-gray-500">Total Qty:</span>{' '}
                    <span className="font-bold">{getTotalActualQty()}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Packages:</span>{' '}
                    <span className="font-bold">{getTotalPackages()}</span>
                  </div>
                </div>
              </div>

              {/* Info Note */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>
                  Marking ready will auto-create a <strong>Gate Pass</strong> for Security verification.
                  Invoice and E-way Bill must be added before the truck can leave.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setLoadingDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleMarkReady} 
              disabled={submitting}
              data-testid="mark-ready-btn"
            >
              {submitting ? 'Processing...' : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark Ready for Dispatch
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shortfall Dialog - appears when actual < planned */}
      <Dialog open={shortfallDialog} onOpenChange={setShortfallDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Shortfall Detected
            </DialogTitle>
            <DialogDescription>
              The loaded quantity is less than planned for {shortfallData.planNumber}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Shortfall Summary */}
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex justify-between items-center mb-3">
                <span className="font-medium text-orange-800">Total Shortfall</span>
                <Badge className="bg-orange-100 text-orange-800 text-lg px-3">
                  {shortfallData.totalShortfall} units
                </Badge>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-orange-700">Item</TableHead>
                    <TableHead className="text-right text-orange-700">Planned</TableHead>
                    <TableHead className="text-right text-orange-700">Loaded</TableHead>
                    <TableHead className="text-right text-orange-700">Short</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shortfallData.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-sm">
                        <div className="font-medium">{item.item_name}</div>
                        {item.item_code && <div className="text-xs text-gray-500">{item.item_code}</div>}
                      </TableCell>
                      <TableCell className="text-right">{item.planned}</TableCell>
                      <TableCell className="text-right">{item.actual}</TableCell>
                      <TableCell className="text-right font-bold text-orange-600">
                        {item.shortfall} {item.unit}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Gate Pass Info */}
            {shortfallData.gatePassNumber && (
              <div className="p-3 bg-green-50 rounded-lg text-sm text-green-800 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Gate Pass <strong>{shortfallData.gatePassNumber}</strong> created for loaded quantity
              </div>
            )}

            {/* Action Options */}
            <div className="space-y-3">
              <p className="text-sm text-gray-600 font-medium">What would you like to do with the shortfall?</p>
              
              {/* Option 1: Load in Another Vehicle */}
              <div className="p-4 border rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Split className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium">Load in Another Vehicle</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Create a new dispatch plan for the shortfall items. The new plan will be scheduled for the same date.
                    </p>
                    <Button 
                      className="mt-3" 
                      onClick={handleCreateSplitPlan}
                      disabled={shortfallSubmitting}
                      data-testid="split-plan-btn"
                    >
                      {shortfallSubmitting ? 'Creating...' : 'Create Split Plan'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Option 2: Request Postponement */}
              <div className="p-4 border rounded-lg hover:border-yellow-300 hover:bg-yellow-50/50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <PauseCircle className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium">Request Postponement</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      Notify Sales team that the shortfall cannot be fulfilled now. They will decide when to reschedule.
                    </p>
                    <div className="mt-3 space-y-2">
                      <Textarea
                        placeholder="Reason for postponement (required)..."
                        value={postponeReason}
                        onChange={(e) => setPostponeReason(e.target.value)}
                        className="text-sm"
                        rows={2}
                        data-testid="postpone-reason-input"
                      />
                      <Button 
                        variant="outline"
                        className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                        onClick={handleRequestPostponement}
                        disabled={shortfallSubmitting || !postponeReason.trim()}
                        data-testid="postpone-btn"
                      >
                        {shortfallSubmitting ? 'Requesting...' : 'Request Postponement'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={handleSkipShortfall} data-testid="skip-shortfall-btn">
              Skip for Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
};

export default LoadingConsolePage;
